import { useState, useEffect } from 'react';
import { View, Pressable, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { FirebaseError } from 'firebase/app';
import { createUserWithEmailAndPassword, sendEmailVerification, signInWithEmailAndPassword } from 'firebase/auth';
import { Ionicons } from '@expo/vector-icons';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { Body, Caption, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { auth } from '@/lib/firebase';
import { useAuthDraftStore } from '@/state/authDraftStore';
import { useUserStore } from '@/state/userStore';
import { humanizeAsyncError } from '@/utils/asyncErrorMessage';
import { getEmailVerificationActionCodeSettings } from '@/utils/emailVerificationActionSettings';
import { logAnalyticsEvent } from '@/services/analyticsLogger';

export default function EmailAuthScreen() {
  const router = useRouter();
  const theme = useTheme();

  const mode = useAuthDraftStore((s) => s.mode);
  const email = useAuthDraftStore((s) => s.email);
  const password = useAuthDraftStore((s) => s.password);
  const confirmPassword = useAuthDraftStore((s) => s.confirmPassword);
  const acceptedTerms = useAuthDraftStore((s) => s.acceptedTerms);
  const confirmedAge = useAuthDraftStore((s) => s.confirmedAge);
  const setMode = useAuthDraftStore((s) => s.setMode);
  const setEmail = useAuthDraftStore((s) => s.setEmail);
  const setPassword = useAuthDraftStore((s) => s.setPassword);
  const setConfirmPassword = useAuthDraftStore((s) => s.setConfirmPassword);
  const setAcceptedTerms = useAuthDraftStore((s) => s.setAcceptedTerms);
  const setConfirmedAge = useAuthDraftStore((s) => s.setConfirmedAge);
  const resetDraft = useAuthDraftStore((s) => s.reset);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [emailError, setEmailError] = useState<string | undefined>();
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | undefined>();

  const authReady = useUserStore((s) => s.authReady);
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const needsEmailVerification = useUserStore((s) => s.needsEmailVerification);
  const hasCompletedOnboarding = useUserStore((s) => s.hasCompletedOnboarding);

  // Leave this route once the profile has finished loading (authReady) and we know the real
  // onboarding state. Waiting for authReady prevents a premature redirect to profile-details
  // when the profile hasn't hydrated yet (hasCompletedOnboarding is still false from reset).
  useEffect(() => {
    if (!authReady || !isAuthenticated) return;
    if (needsEmailVerification) {
      router.replace('/(onboarding)/verify-email');
    } else if (hasCompletedOnboarding) {
      router.replace('/(tabs)');
    } else {
      router.replace('/(onboarding)/profile-details');
    }
  }, [authReady, isAuthenticated, needsEmailVerification, hasCompletedOnboarding, router]);

  // On web, stack scene replacement can briefly keep the previous screen mounted.
  // Once auth is confirmed, hide this form immediately so it never appears "stuck"
  // while NavigationHandler/email-auth effects finish routing to the destination.
  if (isAuthenticated) {
    return null;
  }

  const validatePassword = (value: string) => {
    if (value.length < 8) {
      return 'Password must be at least 8 characters.';
    }

    const hasUppercase = /[A-Z]/.test(value);
    const hasLowercase = /[a-z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSymbol = /[^A-Za-z0-9\s]/.test(value);

    if (!(hasUppercase && hasLowercase && hasNumber && hasSymbol)) {
      return 'Include uppercase, lowercase, numeric, and symbol characters.';
    }

    return undefined;
  };

  const handleAuth = async () => {
    const trimmedEmail = email.trim();
    let nextEmailError: string | undefined;
    let nextPasswordError: string | undefined;
    let nextConfirmPasswordError: string | undefined;

    if (!trimmedEmail) {
      nextEmailError = 'Enter your email address to continue.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      nextEmailError = 'Enter a valid email address (for example, name@example.com).';
    }

    if (!password) {
      nextPasswordError = 'Enter your password to continue.';
    }

    if (mode === 'signup') {
      const passwordIssue = validatePassword(password);
      if (passwordIssue) {
        nextPasswordError = passwordIssue;
      }

      if (!confirmPassword) {
        nextConfirmPasswordError = 'Confirm your password to continue.';
      } else if (password !== confirmPassword) {
        nextConfirmPasswordError = 'Passwords do not match. Please check and try again.';
      }
    }

    setEmailError(nextEmailError);
    setPasswordError(nextPasswordError);
    setConfirmPasswordError(nextConfirmPasswordError);

    if (nextEmailError || nextPasswordError || nextConfirmPasswordError) {
      setError(undefined);
      return;
    }

    if (mode === 'signup') {
      if (!confirmedAge) {
        setError('Please confirm that you are at least 18 years old to create an account.');
        return;
      }

      if (!acceptedTerms) {
        setError('Please accept the Terms of Service and Privacy Policy to create an account.');
        return;
      }

    }

    setLoading(true);
    setError(undefined);

    const doAuth = async (): Promise<void> => {
      if (mode === 'signup') {
        await createUserWithEmailAndPassword(auth, trimmedEmail, password);
      } else {
        await signInWithEmailAndPassword(auth, trimmedEmail, password);
      }
    };

    let authSucceeded = false;
    try {
      await doAuth();
      authSucceeded = true;
      if (mode === 'signup') {
        void logAnalyticsEvent('signup_completed', {
          method: 'email',
          isNewUser: true,
        });
      }
      if (mode === 'signup' && auth.currentUser && !auth.currentUser.emailVerified) {
        try {
          await sendEmailVerification(auth.currentUser, getEmailVerificationActionCodeSettings());
        } catch (verificationError) {
          // Don't block successful account creation on email send retries, but log enough
          // context to diagnose production-only verification delivery failures.
          const verificationErrorDetails =
            verificationError instanceof FirebaseError
              ? `${verificationError.code}: ${verificationError.message}`
              : verificationError instanceof Error
                ? verificationError.message
                : String(verificationError);
          console.error('Failed to send initial verification email', {
            verificationError: verificationErrorDetails,
            projectId: auth.app.options.projectId,
            authDomain: auth.app.options.authDomain,
            userEmail: auth.currentUser?.email ?? trimmedEmail,
          });
        }
      }
    } catch (err: unknown) {
      // On iOS staging, network-request-failed can be transient (timeout). Retry once after a short delay.
      const isNetworkFailed = err instanceof FirebaseError && err.code === 'auth/network-request-failed';
      if (isNetworkFailed && Platform.OS === 'ios' && mode === 'signin') {
        try {
          await new Promise((r) => setTimeout(r, 1500));
          await doAuth();
          authSucceeded = true;
        } catch (retryErr) {
          err = retryErr;
        }
      }

      if (!authSucceeded) {
        console.error('Email auth failed', err);
        if (err instanceof FirebaseError) {
          if (mode === 'signup' && err.code === 'auth/email-already-in-use') {
            setError(
              'We could not create an account with those credentials. Try signing in or resetting your password.',
            );
          } else if (mode === 'signin' && err.code === 'auth/user-not-found') {
            setError(
              'We could not sign you in with those credentials. Check your details or reset your password.',
            );
          } else if (
            mode === 'signin' &&
            (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-email')
          ) {
            setError(
              'Wrong email or password. Check your credentials and try again. If you signed up with Google or Apple, use that option to sign in.',
            );
          } else if (err.code === 'auth/network-request-failed') {
            setError(
              'Network error. Check your connection and try again. If it keeps happening, try switching networks.',
            );
          } else {
            setError(humanizeAsyncError(err, 'We couldn’t sign you in right now. Please try again.'));
          }
        } else if (err instanceof Error) {
          setError(humanizeAsyncError(err, 'We couldn’t sign you in right now. Please try again.'));
        } else {
          setError('We couldn’t sign you in right now. Please try again.');
        }
      }
    } finally {
      if (authSucceeded) {
        setPassword('');
        setConfirmPassword('');
        // Always clear the button loading state: if navigation is slow or the screen does not
        // unmount immediately (common on web), leaving loading=true looks like a hung sign-in.
        setLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  return (
    <Screen
      title={mode === 'signup' ? 'Create your Tripvio account' : 'Welcome back'}
      subtitle={
        mode === 'signup'
          ? 'Use your email to create a secure account. You can enable SSO later.'
          : 'Enter your credentials to continue planning with Tripvio.'
      }
      scrollable={Platform.OS !== 'web'}
      showOfflineIndicator={false}
    >
      <View style={{ gap: theme.spacing(2) }}>
        <InputField
          label="Email"
          placeholder="you@example.com"
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          value={email}
          onChangeText={(value) => {
            setEmail(value);
            if (emailError) setEmailError(undefined);
            if (error) setError(undefined);
          }}
          errorText={emailError}
        />
        <InputField
          label="Password"
          secureTextEntry
          showPasswordToggle
          value={password}
          onChangeText={(value) => {
            setPassword(value);
            if (passwordError) setPasswordError(undefined);
            if (error) setError(undefined);
          }}
          helperText={mode === 'signup' ? "Use 8+ characters with upper/lowercase, number, and symbol." : undefined}
          autoCapitalize="none"
          textContentType={mode === 'signup' ? 'newPassword' : 'password'}
          autoComplete={mode === 'signup' ? 'new-password' : 'password'}
          errorText={passwordError}
        />
        {mode === 'signin' && (
          <View style={{ alignItems: 'flex-end', marginTop: -theme.spacing(0.5) }}>
            <Pressable
              onPress={() => router.push('/(onboarding)/forgot-password')}
              style={({ pressed }) => ({
                paddingVertical: theme.spacing(0.5),
                paddingHorizontal: theme.spacing(0.25),
                opacity: pressed ? 0.7 : 1,
              })}
              accessibilityRole="link"
              accessibilityLabel="Forgot password?"
            >
              <ThemedText variant="caption" color={theme.colors.primary} style={{ textDecorationLine: 'underline', fontSize: 13 }}>
                Forgot password?
              </ThemedText>
            </Pressable>
          </View>
        )}
        {mode === 'signup' && (
          <InputField
            label="Confirm Password"
            secureTextEntry
            showPasswordToggle
            value={confirmPassword}
            onChangeText={(value) => {
              setConfirmPassword(value);
              if (confirmPasswordError) setConfirmPasswordError(undefined);
              if (error) setError(undefined);
            }}
            helperText="Re-enter your password to confirm."
            autoCapitalize="none"
            textContentType="newPassword"
            autoComplete="new-password"
            errorText={confirmPasswordError}
          />
        )}
        {error && <Caption color={theme.colors.error}>{error}</Caption>}

        {mode === 'signup' && (
          <View style={{ gap: theme.spacing(1.5) }}>
            {/* Age Confirmation Checkbox */}
            <Pressable
              onPress={() => setConfirmedAge(!confirmedAge)}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: theme.spacing(1.5),
                padding: theme.spacing(1),
                borderRadius: theme.radii.md,
                opacity: pressed ? 0.7 : 1,
              })}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: confirmedAge }}
              accessibilityLabel="Confirm age 18 or older"
            >
              <View
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: theme.radii.sm,
                  borderWidth: 2,
                  borderColor: confirmedAge ? theme.colors.primary : theme.colors.borderMuted,
                  backgroundColor: confirmedAge ? theme.colors.primary : 'transparent',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 2,
                }}
              >
                {confirmedAge && (
                  <Ionicons name="checkmark" size={16} color={theme.colors.surface} />
                )}
              </View>
              <View style={{ flex: 1 }}>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 13, lineHeight: 18 }}>
                  I confirm that I am at least 18 years old
                </Caption>
              </View>
            </Pressable>

            {/* Terms Acceptance Checkbox */}
            <Pressable
              onPress={() => setAcceptedTerms(!acceptedTerms)}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: theme.spacing(1.5),
                padding: theme.spacing(1),
                borderRadius: theme.radii.md,
                opacity: pressed ? 0.7 : 1,
              })}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: acceptedTerms }}
              accessibilityLabel="Accept Terms of Service and Privacy Policy"
            >
              <View
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: theme.radii.sm,
                  borderWidth: 2,
                  borderColor: acceptedTerms ? theme.colors.primary : theme.colors.borderMuted,
                  backgroundColor: acceptedTerms ? theme.colors.primary : 'transparent',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 2,
                }}
              >
                {acceptedTerms && (
                  <Ionicons name="checkmark" size={16} color={theme.colors.surface} />
                )}
              </View>
              <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 13, lineHeight: 18 }}>
                  I agree to the{' '}
                </Caption>
                <Pressable
                  onPress={(e) => {
                    e.stopPropagation();
                    router.push('/(onboarding)/terms-of-service');
                  }}
                  accessibilityRole="link"
                  accessibilityLabel="Terms of Service"
                  style={{ paddingVertical: theme.spacing(0.5), paddingHorizontal: theme.spacing(0.25), minHeight: 44, justifyContent: 'center' }}
                >
                  <ThemedText
                    variant="caption"
                    color={theme.colors.primary}
                    style={{ textDecorationLine: 'underline', fontSize: 13 }}
                  >
                    Terms of Service
                  </ThemedText>
                </Pressable>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 13 }}> and </Caption>
                <Pressable
                  onPress={(e) => {
                    e.stopPropagation();
                    router.push('/(onboarding)/privacy-policy');
                  }}
                  accessibilityRole="link"
                  accessibilityLabel="Privacy Policy"
                  style={{ paddingVertical: theme.spacing(0.5), paddingHorizontal: theme.spacing(0.25), minHeight: 44, justifyContent: 'center' }}
                >
                  <ThemedText
                    variant="caption"
                    color={theme.colors.primary}
                    style={{ textDecorationLine: 'underline', fontSize: 13 }}
                  >
                    Privacy Policy
                  </ThemedText>
                </Pressable>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 13 }}>.</Caption>
              </View>
            </Pressable>
          </View>
        )}

        <Button 
          label={mode === 'signup' ? 'Create account' : 'Sign in'} 
          onPress={handleAuth} 
          loading={loading}
          disabled={loading || (mode === 'signup' && (!confirmedAge || !acceptedTerms))} 
        />

        <Button
          label={mode === 'signup' ? 'Have an account? Sign in' : 'Need an account? Create one'}
          variant="ghost"
          onPress={() => {
            const nextMode = mode === 'signup' ? 'signin' : 'signup';
            // Reset draft appropriately when switching modes.
            // - keep email (helps users who accidentally picked the wrong mode)
            // - clear passwords + signup-only checkboxes
            resetDraft({
              mode: nextMode,
              email,
            });
            setError(undefined);
          }}
          disabled={loading}
        />

        {mode === 'signin' && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 13, lineHeight: 18 }}>
              By continuing, you agree to{' '}
            </Caption>
            <Pressable
              onPress={() => router.push('/(onboarding)/privacy-policy')}
              accessibilityRole="link"
              accessibilityLabel="Privacy Policy"
              style={{ paddingVertical: theme.spacing(0.5), paddingHorizontal: theme.spacing(0.25), minHeight: 44, justifyContent: 'center' }}
            >
              <ThemedText
                variant="caption"
                color={theme.colors.primary}
                style={{ textDecorationLine: 'underline', fontSize: 13 }}
              >
                Privacy Policy
              </ThemedText>
            </Pressable>
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 13 }}> and </Caption>
            <Pressable
              onPress={() => router.push('/(onboarding)/terms-of-service')}
              accessibilityRole="link"
              accessibilityLabel="Terms of Service"
              style={{ paddingVertical: theme.spacing(0.5), paddingHorizontal: theme.spacing(0.25), minHeight: 44, justifyContent: 'center' }}
            >
              <ThemedText
                variant="caption"
                color={theme.colors.primary}
                style={{ textDecorationLine: 'underline', fontSize: 13 }}
              >
                Terms of Service
              </ThemedText>
            </Pressable>
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 13 }}>.</Caption>
          </View>
        )}
      </View>

      <Button
        label="Back"
        variant="ghost"
        onPress={() => {
          if (typeof router.canGoBack === 'function' && router.canGoBack()) {
            router.back();
            return;
          }
          router.replace('/(onboarding)/sign-in');
        }}
        disabled={loading}
      />
    </Screen>
  );
}


